import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wallet, Plus, Search, Check, X, Eye, 
  Clock, IndianRupee, Calculator, MoreVertical
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const LoansPage = () => {
  const [loans, setLoans] = useState([]);
  const [members, setMembers] = useState([]);
  const [emis, setEmis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [formData, setFormData] = useState({
    member_id: '',
    amount: 100000,
    interest_rate: 1,
    tenure_months: 10
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [loansRes, membersRes] = await Promise.all([
        apiClient.get('/loans'),
        apiClient.get('/members')
      ]);
      setLoans(loansRes.data);
      setMembers(membersRes.data.filter(m => m.is_active));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoanEmis = async (loanId) => {
    try {
      const response = await apiClient.get(`/emis?loan_id=${loanId}`);
      setEmis(response.data);
    } catch (error) {
      toast.error('Failed to fetch EMIs');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const calculateLoan = () => {
    const { amount, interest_rate, tenure_months } = formData;
    // CORRECT CALCULATION:
    // Principal per EMI = Amount / Tenure
    // Interest per EMI = Amount * Interest Rate % (flat monthly interest)
    // Total EMI = Principal + Interest
    const principalPerMonth = amount / tenure_months;
    const interestPerMonth = amount * (interest_rate / 100);
    const emiAmount = principalPerMonth + interestPerMonth;
    const totalInterest = interestPerMonth * tenure_months;
    const totalAmount = amount + totalInterest;
    return { totalInterest, totalAmount, emiAmount: Math.round(emiAmount), principalPerMonth: Math.round(principalPerMonth), interestPerMonth: Math.round(interestPerMonth) };
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/loans', formData);
      toast.success('Loan created successfully');
      setShowAddModal(false);
      setFormData({ member_id: '', amount: 100000, interest_rate: 1, tenure_months: 10 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create loan');
    }
  };

  const handleApproveLoan = async (loanId, status) => {
    try {
      await apiClient.put(`/loans/${loanId}/approve`, { status });
      toast.success(`Loan ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update loan status');
    }
  };

  const handlePayEmi = async (emiId) => {
    try {
      await apiClient.put(`/emis/${emiId}/pay`, {});
      toast.success('EMI marked as paid');
      if (selectedLoan) {
        fetchLoanEmis(selectedLoan.id);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update EMI');
    }
  };

  const handleViewLoan = async (loan) => {
    setSelectedLoan(loan);
    await fetchLoanEmis(loan.id);
    setShowViewModal(true);
  };

  const filteredLoans = loans.filter(loan => {
    const matchesSearch = loan.member_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || loan.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const calc = calculateLoan();

  return (
    <AdminLayout title="Loans" subtitle="Manage loan applications and tracking">
      {/* Create Loan Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Loan</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLoan} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="member">Member</Label>
              <Select value={formData.member_id} onValueChange={(v) => setFormData({ ...formData, member_id: v })}>
                <SelectTrigger data-testid="loan-member-select">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {members.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name} ({member.phone})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Loan Amount (₹)</Label>
              <Input
                id="amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                data-testid="loan-amount-input"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rate">Interest Rate (%/month)</Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.1"
                  value={formData.interest_rate}
                  onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) })}
                  data-testid="loan-rate-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tenure">Tenure (months)</Label>
                <Select value={String(formData.tenure_months)} onValueChange={(v) => setFormData({ ...formData, tenure_months: parseInt(v) })}>
                  <SelectTrigger data-testid="loan-tenure-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 months</SelectItem>
                    <SelectItem value="20">20 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Loan Calculator Preview */}
            <div className="p-4 rounded-xl bg-[#F4F7FB] space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-4 h-4 text-[#2962FF]" />
                <span className="text-sm font-medium text-[#0A2540]">EMI Calculation (Due: 4th Sunday)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#425466]">Principal per EMI:</span>
                <span className="font-medium text-[#0A2540]">{formatCurrency(calc.principalPerMonth)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#425466]">Interest per EMI ({formData.interest_rate}% of principal):</span>
                <span className="font-medium text-[#0A2540]">{formatCurrency(calc.interestPerMonth)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                <span className="text-[#425466] font-medium">Monthly EMI:</span>
                <span className="font-bold text-[#00C853]">{formatCurrency(calc.emiAmount)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                <span className="text-[#425466]">Total Interest ({formData.tenure_months} months):</span>
                <span className="font-medium text-orange-600">{formatCurrency(calc.totalInterest)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#425466]">Total Repayment:</span>
                <span className="font-bold text-[#0A2540]">{formatCurrency(calc.totalAmount)}</span>
              </div>
              <p className="text-xs text-[#425466] mt-2 pt-2 border-t border-gray-200">
                Late payment penalty: ₹100/day after 4th Sunday
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#163A5F]" data-testid="loan-submit-btn">
                Create Loan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Loan Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Loan Details</DialogTitle>
          </DialogHeader>
          {selectedLoan && (
            <div className="space-y-6">
              {/* Loan Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Member</p>
                  <p className="font-semibold text-[#0A2540]">{selectedLoan.member_name}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedLoan.status === 'active' ? 'bg-green-100 text-green-600' :
                    selectedLoan.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                    selectedLoan.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                    'bg-red-100 text-red-600'
                  }`}>
                    {selectedLoan.status}
                  </span>
                </div>
              </div>

              {/* Loan Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-[#0A2540]/5 text-center">
                  <p className="text-sm text-[#425466]">Principal</p>
                  <p className="text-lg font-bold text-[#0A2540]">{formatCurrency(selectedLoan.amount)}</p>
                  <p className="text-xs text-[#425466]">Total: {formatCurrency(selectedLoan.total_amount)}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#00C853]/10 text-center">
                  <p className="text-sm text-[#425466]">Interest (per EMI)</p>
                  <p className="text-lg font-bold text-[#00C853]">{formatCurrency(selectedLoan.amount * 0.01)}</p>
                  <p className="text-xs text-[#425466]">Total: {formatCurrency(selectedLoan.total_interest)}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#2962FF]/10 text-center">
                  <p className="text-sm text-[#425466]">Remaining</p>
                  <p className="text-lg font-bold text-[#2962FF]">{formatCurrency(selectedLoan.remaining_amount)}</p>
                  <p className="text-xs text-[#425466]">{selectedLoan.pending_emis}/{selectedLoan.paid_emis + selectedLoan.pending_emis} EMIs</p>
                </div>
              </div>

              {/* Progress */}
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-[#425466]">Repayment Progress</span>
                  <span className="text-sm font-medium text-[#0A2540]">
                    {selectedLoan.paid_emis} / {selectedLoan.paid_emis + selectedLoan.pending_emis} EMIs
                  </span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#00C853] rounded-full transition-all"
                    style={{ width: `${(selectedLoan.paid_emis / (selectedLoan.paid_emis + selectedLoan.pending_emis)) * 100}%` }}
                  />
                </div>
              </div>

              {/* EMI Schedule */}
              {emis.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#0A2540] mb-4">EMI Schedule (Due: 4th Sunday)</h4>
                  <div className="max-h-64 overflow-y-auto rounded-xl border">
                    <table className="w-full">
                      <thead className="bg-[#F4F7FB] sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-[#425466]">#</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-[#425466]">Due Date (4th Sun)</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-[#425466]">EMI Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-[#425466]">Penalty</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-[#425466]">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-[#425466]">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {emis.map((emi) => (
                          <tr key={emi.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{emi.emi_number}</td>
                            <td className="px-4 py-3 text-sm">{new Date(emi.due_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</td>
                            <td className="px-4 py-3 text-sm font-medium">{formatCurrency(emi.amount)}</td>
                            <td className="px-4 py-3 text-sm">
                              {emi.penalty_amount > 0 ? (
                                <span className="text-red-600 font-medium">{formatCurrency(emi.penalty_amount)}</span>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                emi.status === 'paid' ? 'bg-green-100 text-green-600' :
                                emi.status === 'overdue' ? 'bg-red-100 text-red-600' :
                                'bg-yellow-100 text-yellow-600'
                              }`}>
                                {emi.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {emi.status !== 'paid' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handlePayEmi(emi.id)}
                                  className="bg-[#00C853] hover:bg-[#009624] h-7 text-xs"
                                >
                                  Mark Paid
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
        <div className="flex gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#425466]" />
            <Input
              placeholder="Search loans..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-loans-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="loan-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-[#0A2540] hover:bg-[#163A5F]"
          data-testid="create-loan-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Loan
        </Button>
      </div>

      {/* Loans Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="dashboard-card overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F4F7FB] border-b border-gray-100">
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Member</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Amount</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">EMI</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Progress</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><div className="skeleton h-10 w-40 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-4 w-32 rounded-full"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-16 rounded-full"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-8 w-8 rounded"></div></td>
                  </tr>
                ))
              ) : filteredLoans.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-[#425466]">
                    <Wallet className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No loans found</p>
                  </td>
                </tr>
              ) : (
                filteredLoans.map((loan) => (
                  <tr key={loan.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#2962FF]/10 flex items-center justify-center">
                          <Wallet className="w-5 h-5 text-[#2962FF]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#0A2540]">{loan.member_name}</p>
                          <p className="text-xs text-[#425466]">{loan.tenure_months} months @ {loan.interest_rate}%</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-[#0A2540]">{formatCurrency(loan.amount)}</p>
                      <p className="text-xs text-[#425466]">Total: {formatCurrency(loan.total_amount)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-[#00C853]">{formatCurrency(loan.emi_amount)}</p>
                      <p className="text-xs text-[#425466]">/month</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="w-32">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-[#425466]">{loan.paid_emis}/{loan.paid_emis + loan.pending_emis}</span>
                          <span className="text-[#425466]">{Math.round((loan.paid_emis / (loan.paid_emis + loan.pending_emis)) * 100)}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#00C853] rounded-full"
                            style={{ width: `${(loan.paid_emis / (loan.paid_emis + loan.pending_emis)) * 100}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        loan.status === 'active' ? 'bg-green-100 text-green-600' :
                        loan.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                        loan.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                        'bg-red-100 text-red-600'
                      }`}>
                        {loan.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {loan.status === 'pending' ? (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleApproveLoan(loan.id, 'approved')}
                            className="bg-[#00C853] hover:bg-[#009624] h-8"
                            data-testid={`approve-loan-${loan.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleApproveLoan(loan.id, 'rejected')}
                            className="h-8 border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`reject-loan-${loan.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewLoan(loan)}
                          data-testid={`view-loan-${loan.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="dashboard-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
            <Clock className="w-6 h-6 text-yellow-600" />
          </div>
          <div>
            <p className="text-sm text-[#425466]">Pending</p>
            <p className="text-xl font-bold text-yellow-600">{loans.filter(l => l.status === 'pending').length}</p>
          </div>
        </div>
        <div className="dashboard-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-[#425466]">Active</p>
            <p className="text-xl font-bold text-green-600">{loans.filter(l => l.status === 'active').length}</p>
          </div>
        </div>
        <div className="dashboard-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
            <Wallet className="w-6 h-6 text-gray-600" />
          </div>
          <div>
            <p className="text-sm text-[#425466]">Closed</p>
            <p className="text-xl font-bold text-gray-600">{loans.filter(l => l.status === 'closed').length}</p>
          </div>
        </div>
        <div className="dashboard-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#2962FF]/10 flex items-center justify-center">
            <IndianRupee className="w-6 h-6 text-[#2962FF]" />
          </div>
          <div>
            <p className="text-sm text-[#425466]">Total Disbursed</p>
            <p className="text-xl font-bold text-[#2962FF]">
              {formatCurrency(loans.filter(l => ['active', 'closed'].includes(l.status)).reduce((sum, l) => sum + l.amount, 0))}
            </p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LoansPage;
